import { useState } from 'react'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { Search, Plus } from 'lucide-react'

interface Product {
  id: string
  name: string
  sku: string
  price: number | string
  stock_quantity: number
  category_name?: string
}

interface ProductSearchProps {
  products: Product[]
  onAddToCart: (product: Product) => void
  formatCurrency: (amount: number) => string
  className?: string
  hideLabel?: boolean
  onQuickAdd?: () => void
}

export const ProductSearch = ({
  products,
  onAddToCart,
  formatCurrency,
  className,
  hideLabel = false,
  onQuickAdd
}: ProductSearchProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)

  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <Card className={`p-4 ${className || ''}`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search and add products to cart..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setShowDropdown(e.target.value.length > 0)
            }}
            onFocus={() => setShowDropdown(searchTerm.length > 0)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            className="pl-10"
          />
        
        {/* Autocomplete Dropdown */}
        {showDropdown && searchTerm && (
          <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {filteredProducts.slice(0, 8).map(product => (
              <div
                key={product.id}
                onClick={() => {
                  onAddToCart(product)
                  setSearchTerm('')
                  setShowDropdown(false)
                }}
                className="p-3 hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-sm text-gray-900">{product.name}</div>
                    <div className="text-xs text-gray-500">
                      {product.sku} • {product.category_name}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-orange-600">
                      {formatCurrency(parseFloat(product.price?.toString() || '0'))}
                    </div>
                    <div className="text-xs text-gray-500">
                      Stock: {product.stock_quantity}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filteredProducts.length === 0 && (
              <div className="p-3 text-sm text-gray-500 text-center">No products found</div>
            )}
          </div>
        )}
        </div>

        {/* Add Custom Button */}
        {onQuickAdd && (
          <Button
            onClick={onQuickAdd}
            size="sm"
            className="flex items-center gap-1 h-10 px-4 text-xs bg-orange-600 hover:bg-orange-700 whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            Add Custom
          </Button>
        )}
      </div>
    </Card>
  )
}